import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, map } from 'rxjs';

import {
  BreadcrumbItem,
  CmsUrl,
  Facility,
  PrimaryFooterLink,
  ServiceDetails,
} from '@digital-platform/rs-cms/domain';
import { RichTextLinkComponent } from '@digital-platform/rs-cms/feature-ptypes';
import { BingMapComponent } from '@digital-platform/rs-maps/feature-bing-map';
import { MetaDataService } from '@digital-platform/rs-meta/services';
import { BreadcrumbsComponent } from '@digital-platform/rs-shared/ui';
import { IconComponent } from '@digital-platform/rs-shared/ui-brand';
import { FacilityContactUsComponent } from '../facility-contact-us/facility-contact-us.component';
import { FacilityServicesAvailableComponent } from '../facility-services-available/facility-services-available.component';

const emptyFacility: Facility = {
  facility_name: '',
  facility_types: null,
  address: '',
  city: '',
  state: '',
  zip_code: '',
  country: '',
  region: null,
  facility_description: null,
  primary_contact: null,
  email: null,
  website_link: null,
  phone_number: null,
  epa_id: null,
  pin_categories: null,
  services: null,
  latitude: null,
  longitude: null,
};
const emptyUrl: CmsUrl = [
  {
    uri: '',
    title: '',
    options: {
      attributes: {
        target: -1,
      },
    },
  },
];
const emptyWebsiteLink: PrimaryFooterLink = {
  type: 'primaryFooterLink',
  aTarget: null,
  url: emptyUrl,
  loggedInUrl: emptyUrl,
  status: false,
};

@Component({
  selector: 'digital-platform-facility',
  imports: [
    CommonModule,
    FacilityContactUsComponent,
    BingMapComponent,
    FacilityServicesAvailableComponent,
    RichTextLinkComponent,
    BreadcrumbsComponent,
    IconComponent,
  ],
  standalone: true,
  templateUrl: './facility.component.html',
  styleUrls: ['./facility.component.scss'],
})
export class FacilityComponent implements OnInit {
  facility$: Observable<Facility> = new Observable<Facility>();
  facility: Facility = emptyFacility;
  isFacilityMap = true;
  public servicesAvailable: ServiceDetails[] = [];
  public websiteLinks: PrimaryFooterLink[] = [];

  public breadcrumbs: BreadcrumbItem[] = [
    {
      type: 'breadcrumbItem',
      status: true,
      linkTitle: 'Find a Facility',
      linkUrl: '/facilities/environmental-solutions',
    },
  ];

  private metaDataService = inject(MetaDataService);

  constructor(
    private elementRef: ElementRef,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.facility$ = this.route.data.pipe(map((data) => data['facility']));

    this.facility$.subscribe((facility) => {
      if (!facility) {
        this.router.navigateByUrl('/404');
      } else {
        this.facility = facility;
        this.populateBreadcrumb(facility);
        this.setMetaData(facility);

        if (facility.website_link) {
          facility.website_link.forEach((websiteLink) => {
            const websiteLinkObj: PrimaryFooterLink = emptyWebsiteLink;

            if (websiteLink.aTarget) {
              websiteLinkObj.aTarget = websiteLink.aTarget;
            }
            if (websiteLink.url) {
              websiteLinkObj.url = [websiteLink.url[0] || emptyUrl];
            }
            if (websiteLink.loggedInUrl) {
              websiteLinkObj.loggedInUrl = [
                websiteLink.loggedInUrl[0] || emptyUrl,
              ];
            }

            this.websiteLinks.push(websiteLinkObj);
          });
        }
      }
    });
  }

  cleanHtml(text = ''): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(text);
  }

  populateBreadcrumb(facility: Facility): void {
    this.breadcrumbs.push({
      type: 'breadcrumbItem',
      status: false,
      linkTitle: facility.facility_name,
      linkUrl: '',
    });
  }

  private setMetaData(facility: Facility): void {
    const { facility_name: title } = facility;

    this.metaDataService.setMetaData({
      title,
      description: `Republic Services has emergency response, field & industrial, treatment & disposal, and exploration & production facilities nationwide. Learn more about the services offered at ${title}.`,
      canonical_url: `https://www.republicservices.com${this.router.url}`,
    });
  }

  formatAddress(facility: Facility): string {
    const baseUrl = 'https://www.google.com/maps/search/';
    const { address, city, state, zip_code } = facility;
    return `${baseUrl}${this.decodeHtml(address)},${city},${state},${zip_code}`;
  }

  formatPhoneNumber(facility: Facility): string {
    return `tel:+${facility.phone_number?.replace(/\D/g, '') ?? ''}`;
  }

  decodeHtml(encodedString: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = encodedString;
    return textarea.value;
  }
}
